import React, { useState, useEffect, useCallback } from 'react';
import { studentsAPI } from '../../services/api';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import StatusBadge from '../../components/common/StatusBadge';
import toast from 'react-hot-toast';
import { formatDate } from '../../utils/helpers';

/* ─────────────────────────────────────────
   Helper: empty form objects
───────────────────────────────────────── */
const emptyClass = { name: '', numeric_value: '' };
const emptySection = { name: '', class_obj: '', academic_year: '', max_students: 40 };
const emptyYear = {
  year: '',
  start_date: '',
  end_date: '',
  is_current: false,
};

/* ─────────────────────────────────────────
   Inline Field component
───────────────────────────────────────── */
const Field = ({ label, name, type = 'text', value, onChange, required, options, col = 6 }) => (
  <div className={`col-md-${col} mb-3`}>
    <label className="form-label">
      {label}{required && <span className="text-danger ms-1">*</span>}
    </label>
    {options ? (
      <select
        className="form-select"
        name={name}
        value={value ?? ''}
        onChange={onChange}
        required={required}
      >
        <option value="">— Select {label} —</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    ) : type === 'checkbox' ? (
      <div className="form-check mt-2">
        <input
          className="form-check-input"
          type="checkbox"
          id={name}
          name={name}
          checked={!!value}
          onChange={onChange}
        />
        <label className="form-check-label" htmlFor={name}>Yes</label>
      </div>
    ) : (
      <input
        type={type}
        className="form-control"
        name={name}
        value={value ?? ''}
        onChange={onChange}
        required={required}
      />
    )}
  </div>
);

/* ═══════════════════════════════════════════
   TAB 1 — Classes
═══════════════════════════════════════════ */
const ClassesTab = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyClass);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    studentsAPI.classes()
      .then(r => setClasses(r.data.results || r.data))
      .catch(() => toast.error('Failed to load classes'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditItem(null); setForm(emptyClass); setShowModal(true); };
  const openEdit = (item) => { setEditItem(item); setForm({ name: item.name, numeric_value: item.numeric_value }); setShowModal(true); };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editItem) {
        await studentsAPI.updateClass(editItem.id, form);
        toast.success('Class updated!');
      } else {
        await studentsAPI.createClass(form);
        toast.success('Class created!');
      }
      setShowModal(false);
      load();
    } catch (err) {
      const errors = err.response?.data;
      if (errors) Object.entries(errors).forEach(([k, v]) => toast.error(`${k}: ${Array.isArray(v) ? v[0] : v}`));
      else toast.error('Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this class? This will affect all related sections and students.')) return;
    try {
      await studentsAPI.deleteClass(id);
      toast.success('Class deleted');
      load();
    } catch { toast.error('Cannot delete — class may have linked records'); }
  };

  const columns = [
    {
      key: 'numeric_value', label: '#',
      render: v => (
        <span className='bg-warning rounded min-width-40'>{v}</span>
      ),
    },
    { key: 'name', label: 'Class Name', render: v => <strong style={{ fontSize: 14 }}>{v}</strong> },
    {
      key: 'id', label: 'Sections',
      render: (id, row) => {
        const count = row.sections_count ?? '—';
        return (
          <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
            {count} sections
          </span>
        );
      },
    },
    {
      key: 'id', label: 'Actions',
      render: (id, row) => (
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-outline-primary" title='Edit' onClick={e => { e.stopPropagation(); openEdit(row); }}><i className="bi bi-pencil-square"></i></button>
          <button className="btn btn-sm btn-outline-danger" title='Delete' onClick={e => { e.stopPropagation(); handleDelete(id); }}><i className="bi bi-trash"></i></button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0 fw-bold"><i class="bi bi-building icon-md me-2 text-info"></i> Classes</h5>
          <small className="text-muted">Manage grade/class definitions</small>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>➕ Add Class</button>
      </div>

      {/* Summary cards */}
      <div className="row g-3 mb-4">
        {[
          { label: 'Total Classes', value: classes.length, bg: '#eff6ff', color: '#1d4ed8', icon: <i class="bi bi-buildings icon-lg"></i> },
          { label: 'Lowest Class', value: classes[0]?.name || '—', bg: '#f0fdf4', color: '#15803d', icon: '1️⃣' },
          { label: 'Highest Class', value: classes[classes.length - 1]?.name || '—', bg: '#faf5ff', color: '#7c3aed', icon: '🎓' },
        ].map(s => (
          <div key={s.label} className="col-md-4">
            <div style={{ background: s.bg, borderRadius: 12, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ fontSize: 26 }}>{s.icon}</span>
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{s.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <span>📋 All Classes ({classes.length})</span>
        </div>
        <DataTable columns={columns} data={classes} loading={loading} />
      </div>

      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        title={editItem ? `Edit Class — ${editItem.name}` : 'Add New Class'}
        footer={
          <div className="d-flex gap-2 justify-content-end w-100">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>Cancel</button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving}
              onClick={handleSave}
            >
              {saving
                ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</>
                : editItem ? '💾 Update Class' : '➕ Create Class'}
            </button>
          </div>
        }
      >
        <form onSubmit={handleSave}>
          <div className="row">
            <Field label="Class Name" name="name" value={form.name} onChange={handleChange} required
              col={8}
            />
            <Field label="Order / Numeric Value" name="numeric_value" type="number" value={form.numeric_value}
              onChange={handleChange} required col={4}
            />
          </div>
          <div className="alert alert-info" style={{ fontSize: 13 }}>
            💡 <strong>Numeric value</strong> controls the sort order in dropdowns and reports (e.g. Class 1 = 1, Class 10 = 10).
          </div>
        </form>
      </Modal>
    </>
  );
};

/* ═══════════════════════════════════════════
   TAB 2 — Sections
═══════════════════════════════════════════ */
const SectionsTab = ({ classes, academicYears }) => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterClass, setFilterClass] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptySection);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filterClass) params.class_obj = filterClass;
    if (filterYear) params.academic_year = filterYear;
    studentsAPI.sections(params)
      .then(r => setSections(r.data.results || r.data))
      .catch(() => toast.error('Failed to load sections'))
      .finally(() => setLoading(false));
  }, [filterClass, filterYear]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditItem(null); setForm(emptySection); setShowModal(true); };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      name: item.name,
      class_obj: item.class_obj,
      academic_year: item.academic_year,
      max_students: item.max_students,
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editItem) {
        await studentsAPI.updateSection(editItem.id, form);
        toast.success('Section updated!');
      } else {
        await studentsAPI.createSection(form);
        toast.success('Section created!');
      }
      setShowModal(false);
      load();
    } catch (err) {
      const errors = err.response?.data;
      if (errors) Object.entries(errors).forEach(([k, v]) => toast.error(`${k}: ${Array.isArray(v) ? v[0] : v}`));
      else toast.error('Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this section?')) return;
    try {
      await studentsAPI.deleteSection(id);
      toast.success('Section deleted');
      load();
    } catch { toast.error('Cannot delete — section may have linked records'); }
  };

  const columns = [
    {
      key: 'name', label: 'Section',
      render: v => (
        <span style={{
          minWidth: 36, height: 30, borderRadius: 6,
          background: 'linear-gradient(135deg,#f59e0b,#d97706)',
          color: '#fff', fontWeight: 500, fontSize: 14,padding:"0px 5px",
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>{v}</span>
      ),
    },
    { key: 'class_name', label: 'Class', render: v => <strong>{v}</strong> },
    {
      key: 'max_students', label: 'Capacity',
      render: v => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13 }}>👤</span>
          <span style={{ fontWeight: 600 }}>{v} students</span>
        </span>
      ),
    },
    {
      key: 'academic_year', label: 'Academic Year',
      render: (v, row) => {
        const yr = academicYears.find(y => y.id === v);
        return yr
          ? <span style={{ background: yr.is_current ? '#d1fae5' : '#f1f5f9', color: yr.is_current ? '#065f46' : '#475569', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
              {yr.year}{yr.is_current ? ' ✦' : ''}
            </span>
          : '—';
      },
    },
    {
      key: 'id', label: 'Actions',
      render: (id, row) => (
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-outline-primary" title='Edit' onClick={e => { e.stopPropagation(); openEdit(row); }}><i className="bi bi-pencil-square"></i></button>
          <button className="btn btn-sm btn-outline-danger" title='Delete' onClick={e => { e.stopPropagation(); handleDelete(id); }}><i className="bi bi-trash"></i></button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0 fw-bold"><i className="bi bi-folder icon-md me-2 text-warning"></i> Sections</h5>
          <small className="text-muted">Sections within each class per academic year</small>
        </div>
        <button className="btn btn-primary content-center" onClick={openAdd}><i class="bi bi-folder-plus icon-sm me-2"></i> Add Section</button>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body py-3">
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <label className="form-label">Filter by Class</label>
              <select className="form-select" value={filterClass} onChange={e => setFilterClass(e.target.value)}>
                <option value="">All Classes</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Filter by Academic Year</label>
              <select className="form-select" value={filterYear} onChange={e => setFilterYear(e.target.value)}>
                <option value="">All Years</option>
                {academicYears.map(y => <option key={y.id} value={y.id}>{y.year}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <button className="btn btn-outline-secondary w-100 content-center"
                onClick={() => { setFilterClass(''); setFilterYear(''); }}>
                <i class="lni lni-refresh-circle-1-clockwise icon-md me-2"></i> Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span>📋 Sections ({sections.length})</span>
        </div>
        <DataTable columns={columns} data={sections} loading={loading} />
      </div>

      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        title={editItem ? 'Edit Section' : 'Add New Section'}
        footer={
          <div className="d-flex gap-2 justify-content-end w-100">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>Cancel</button>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={handleSave}>
              {saving ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</> : editItem ? '💾 Update Section' : '➕ Create Section'}
            </button>
          </div>
        }
      >
        <form onSubmit={handleSave}>
          <div className="row">
            <Field label="Section Name" name="name" value={form.name} onChange={handleChange} required col={4}
            />
            <Field
              label="Class" name="class_obj" value={form.class_obj} onChange={handleChange} required col={4}
              options={classes.map(c => ({ value: c.id, label: c.name }))}
            />
            <Field
              label="Academic Year" name="academic_year" value={form.academic_year} onChange={handleChange} required col={4}
              options={academicYears.map(y => ({ value: y.id, label: y.year + (y.is_current ? ' (Current)' : '') }))}
            />
            <Field label="Max Students" name="max_students" type="number" value={form.max_students}
              onChange={handleChange} required col={4}
            />
          </div>
        </form>
      </Modal>
    </>
  );
};

/* ═══════════════════════════════════════════
   TAB 3 — Academic Years
═══════════════════════════════════════════ */
const AcademicYearsTab = ({ onYearsChange }) => {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyYear);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    studentsAPI.academicYears()
      .then(r => {
        const data = r.data.results || r.data;
        setYears(data);
        onYearsChange && onYearsChange(data);
      })
      .catch(() => toast.error('Failed to load academic years'))
      .finally(() => setLoading(false));
  }, [onYearsChange]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditItem(null); setForm(emptyYear); setShowModal(true); };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({ year: item.year, start_date: item.start_date, end_date: item.end_date, is_current: item.is_current });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editItem) {
        await studentsAPI.updateAcademicYear(editItem.id, form);
        toast.success('Academic year updated!');
      } else {
        await studentsAPI.createAcademicYear(form);
        toast.success('Academic year created!');
      }
      setShowModal(false);
      load();
    } catch (err) {
      const errors = err.response?.data;
      if (errors) Object.entries(errors).forEach(([k, v]) => toast.error(`${k}: ${Array.isArray(v) ? v[0] : v}`));
      else toast.error('Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this academic year?')) return;
    try {
      await studentsAPI.deleteAcademicYear(id);
      toast.success('Deleted');
      load();
    } catch { toast.error('Cannot delete — year may have linked records'); }
  };

  const columns = [
    {
      key: 'year', label: 'Academic Year',
      render: (v, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <strong style={{ fontSize: 15 }}>{v}</strong>
          {row.is_current && (
            <span style={{ background: '#d1fae5', color: '#065f46', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>
              ✦ Current
            </span>
          )}
        </div>
      ),
    },
    { key: 'start_date', label: 'Start Date', render: v => formatDate(v) },
    { key: 'end_date', label: 'End Date', render: v => formatDate(v) },
    {
      key: 'is_current', label: 'Status',
      render: v => <StatusBadge status={v ? 'active' : 'inactive'} />,
    },
    {
      key: 'id', label: 'Actions',
      render: (id, row) => (
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-outline-primary" title='Edit' onClick={e => { e.stopPropagation(); openEdit(row); }}><i className="bi bi-pencil-square"></i></button>
          <button className="btn btn-sm btn-outline-danger" title='Delete' onClick={e => { e.stopPropagation(); handleDelete(id); }}><i className="bi bi-trash"></i></button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0 fw-bold gap-3"><i className="bi bi-calendar4-week icon-md me-2 text-success"></i> Academic Years</h5>
          <small className="text-muted">Define school years and mark the current active year</small>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>➕ Add Year</button>
      </div>

      <div className="card">
        <div className="card-header">
          <span>📋 Academic Years ({years.length})</span>
        </div>
        <DataTable columns={columns} data={years} loading={loading} />
      </div>

      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        title={editItem ? `Edit Year — ${editItem.year}` : 'Add Academic Year'}
        footer={
          <div className="d-flex gap-2 justify-content-end w-100">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>Cancel</button>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={handleSave}>
              {saving ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</> : editItem ? '💾 Update' : '➕ Create'}
            </button>
          </div>
        }
      >
        <form onSubmit={handleSave}>
          <div className="row">
            <Field label="Year Label (e.g. 2024-2025)" name="year" value={form.year}
              onChange={handleChange} required col={12}
            />
            <Field label="Start Date" name="start_date" type="date" value={form.start_date}
              onChange={handleChange} required col={6}
            />
            <Field label="End Date" name="end_date" type="date" value={form.end_date}
              onChange={handleChange} required col={6}
            />
            <Field label="Set as Current Year" name="is_current" type="checkbox"
              value={form.is_current} onChange={handleChange} col={12}
            />
          </div>
          {form.is_current && (
            <div className="alert alert-warning" style={{ fontSize: 13 }}>
              ⚠️ Marking this as the current year will be reflected across attendance, fees, and sections.
            </div>
          )}
        </form>
      </Modal>
    </>
  );
};

/* ═══════════════════════════════════════════
   ROOT EXPORT — ClassesManagement
═══════════════════════════════════════════ */
const ClassesManagement = () => {
  const [tab, setTab] = useState('years');
  const [classes, setClasses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);

  // Pre-load classes for the sections tab
  useEffect(() => {
    studentsAPI.classes()
      .then(r => setClasses(r.data.results || r.data))
      .catch(() => {});
  }, []);

  const tabs = [
    { key: 'years', icon: <i className="bi bi-calendar4-week icon-sm"></i>, label: 'Academic Years' },
    { key: 'classes', icon: <i className="bi bi-building icon-sm"></i>, label: 'Classes' },
    { key: 'sections', icon: <i className="bi bi-folder icon-sm"></i>, label: 'Sections' },
  ];

  return (
    <div>
      {/* Page Header */}
      {/* <div className="page-header">
        <div>
          <h2>Classes Management</h2>
          <p>Manage classes, sections, and academic year configuration</p>
        </div>
      </div> */}

      {/* Tab Bar */}
      <div
        style={{
          display: 'flex', gap: 4, marginBottom: 24,
          background: '#f1f5f9', borderRadius: 12, padding: 4,
          width: 'fit-content',
        }}
      >
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              border: 'none', cursor: 'pointer',
              padding: '8px 20px', borderRadius: 10,
              fontWeight: 600, fontSize: 13,
              transition: 'all 0.2s',
              background: tab === t.key ? '#fff' : 'transparent',
              color: tab === t.key ? '#1a3a5c' : '#64748b',
              boxShadow: tab === t.key ? '0 1px 6px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'classes' && <ClassesTab />}
      {tab === 'sections' && (
        <SectionsTab classes={classes} academicYears={academicYears} />
      )}
      {tab === 'years' && (
        <AcademicYearsTab onYearsChange={setAcademicYears} />
      )}
    </div>
  );
};

export default ClassesManagement;
